import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { fromJS, Map, List } from 'immutable'
import TileVideo from '../TileVideo/index'
import TileEpisode from '../TileEpisode/index'
import TileSeries from '../TileSeries/index'
import TileSubcategory from '../TileSubcategory/index'
import TileSeriesLink from '../TileSeriesLink/index'
import TilePlaceholder from '../TilePlaceholder/index'
import TileProduct from '../TileProduct/index'
import LabelFree from '../LabelFree/index'
import { hasLiveAccessEntitlement, isEntitled } from '../../services/subscription/index'
import { ACCESS_PREVIEW, ACCESS_FEATURE } from '../WatchAccess/index'
import { isLiveAccessFeature } from '../../services/live-access-feature/index'

import {
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_VIDEO_YOGA,
  TYPE_CONTENT_VIDEO_FITNESS,
  TYPE_CONTENT_VIDEO_MEDITATION,
  TYPE_CONTENT_EPISODE,
  TYPE_CONTENT_EPISODE_YOGA,
  TYPE_CONTENT_EPISODE_FITNESS,
  TYPE_CONTENT_EPISODE_MEDITATION,
  TYPE_CONTENT_SEGMENT,
  TYPE_CONTENT_SEGMENT_YOGA,
  TYPE_CONTENT_SEGMENT_FITNESS,
  TYPE_CONTENT_SEGMENT_MEDITATION,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_SERIES_YOGA,
  TYPE_CONTENT_SERIES_FITNESS,
  TYPE_CONTENT_SERIES_MEDITATION,
  TYPE_CONTENT_SERIES_LINK,
  TYPE_CONTENT_SEGMENTED,
  TYPE_CONTENT_SEGMENTED_YOGA,
  TYPE_CONTENT_SEGMENTED_FITNESS,
  TYPE_CONTENT_SEGMENTED_MEDITATION,
  TYPE_CONTENT_UNKNOWN,
  TYPE_CONTENT_SUBCATEGORY,
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_EPISODE_GUIDE,
  TYPE_CONTENT_SERIES_GUIDE,
  isGuideType,
  isEventType,
} from '../../services/content-type/index'

function getClassName (
  inRow,
  single,
  tileClass,
  playlistEditComponent,
  hideWatchedEditComponent,
) {
  const className = ['tile']
  if (inRow) {
    className.push('tile--row')
  } else if (single) {
    className.push('tile--single')
  } else {
    className.push('tile--gallery')
  }
  if (playlistEditComponent || hideWatchedEditComponent) {
    className.push('tile--edit-mode')
  }
  className.push(tileClass)
  return className.join(' ')
}

function getContentClassName (active, type) {
  return ['tile__content']
    .concat(
      active && type !== TYPE_CONTENT_SERIES_LINK
        ? 'tile__content--active'
        : [],
    )
    .join(' ')
}

// eslint-disable-next-line max-len
function getLabel (staticText, type, isNew, featuredType, featuredLabel, hasNewEpisodes, isGuide, isEvent, options = {}) {
  const { isLiveAccessUpgradeToWatch } = options
  if (featuredType === 'merchandising-campaign') {
    return featuredLabel
  }

  switch (type) {
    case TYPE_CONTENT_VIDEO:
      return isNew ? staticText.get('newVideo') : null

    case TYPE_CONTENT_EPISODE:
      if (isLiveAccessUpgradeToWatch) {
        return staticText.get('gaiaPlus')
      }
      switch (featuredType) {
        case 'new':
          return staticText.get('newEpisode')
        case 'next':
          return staticText.get('nextEpisode')
        case 'first':
          return staticText.get('firstEpisode')
        case 'latest':
          return staticText.get('latestEpisode')
        case 'last':
          return staticText.get('lastEpisode')
        case 'continue':
          return staticText.get('continueWatching')
        default:
          return isNew ? staticText.get('newEpisode') : null
      }

    case TYPE_CONTENT_SERIES:
      if (hasNewEpisodes) {
        return staticText.get('newEpisode')
      } if (isNew) {
        return staticText.get('new')
      } if (isGuide || isEvent) {
        return staticText.get('gaiaPlus')
      }
      return null

    default:
      return null
  }
}

function getTypeContent (data) {
  const isProduct = data.getIn(['contentType']) === TYPE_CONTENT_PRODUCT
  const type = data.getIn(['type', 'content'])
    ? data.getIn(['type', 'content'])
    : data.get('type', TYPE_CONTENT_UNKNOWN)
  return isProduct ? TYPE_CONTENT_PRODUCT : type
}

/**
 * @type {React.Component}
 */
function TitleFreeLabel (props) {
  const { tileData, auth, isFree } = props
  const contentType = tileData.getIn(['type', 'content'])
  const userSubscriptions = auth.get('subscriptions', List())
  const featureOfferingAvailibility = tileData.getIn(['feature', 'offerings', 'availability'])
  if (contentType === TYPE_CONTENT_SUBCATEGORY) {
    return null
  }
  return (
    <LabelFree
      featureOfferingAvailibility={featureOfferingAvailibility}
      userSubscriptions={userSubscriptions}
      contentType={contentType}
      isFree={isFree}
    />
  )
}

/**
 * @type {React.Component}
 */
function TileContent (props) {
  const {
    tileData: data,
    staticText,
    displayMoreInfoButton,
    addToPlaylist = false,
    showTeaser,
    onClickWatch,
    vertical = false,
    forceAccess,
    asShare,
    auth,
    itemIndex,
    fullContext,
    uiLocation,
  } = props
  const type = getTypeContent(data)

  const isInPlaylist = data.getIn(['userInfo', 'playlist'], false)
  const isGuide = isGuideType(data)
  const isEvent = isEventType(data)

  const id = data.get('id')
  const subscriptions = auth.get('subscriptions', List())
  const isLiveAccessUpgradeToWatch = isLiveAccessFeature(data.get('feature', Map()))
        && isEntitled(subscriptions) && !hasLiveAccessEntitlement(subscriptions)
  switch (type) {
    case TYPE_CONTENT_VIDEO:
    case TYPE_CONTENT_VIDEO_YOGA:
    case TYPE_CONTENT_VIDEO_FITNESS:
    case TYPE_CONTENT_VIDEO_MEDITATION:
      return (
        <TileVideo
          asShare={asShare}
          onClickWatch={onClickWatch}
          displayMoreInfoButton={displayMoreInfoButton}
          auth={props.auth}
          page={props.page}
          active={props.active}
          showMoreInfo={props.showMoreInfo}
          type={data.getIn(['type', 'content'])}
          heroLabel={props.heroLabel}
          title={data.get('title')}
          image={data.get('image')}
          vote={data.get('vote')}
          voteDown={data.get('voteDown')}
          forceAccess={forceAccess}
          voteId={id}
          id={id}
          label={getLabel(
            staticText,
            TYPE_CONTENT_VIDEO,
            data.get('isNew'),
            data.get('featuredTileType'),
            data.get('featuredTileLabel'),
            null,
            isGuide,
            isEvent,
          )}
          teaser={showTeaser ? data.get('teaser') : null}
          duration={data.get('duration')}
          userInfo={data.get('userInfo')}
          url={data.get('url', '')}
          host={data.get('host')}
          yogaStyle={data.get('yogaStyle')}
          yogaLevel={data.get('yogaLevel')}
          yogaLevelPath={data.get('yogaLevelPath')}
          yogaStylePath={data.get('yogaStylePath')}
          yogaDurationPath={data.get('yogaDurationPath')}
          yogaTeacherPath={data.get('yogaTeacherPath')}
          fitnessStyle={data.get('fitnessStyle')}
          fitnessLevel={data.get('fitnessLevel')}
          fitnessLevelPath={data.get('fitnessLevelPath')}
          fitnessStylePath={data.get('fitnessStylePath')}
          fitnessDurationPath={data.get('fitnessDurationPath')}
          fitnessInstructorPath={data.get('fitnessInstructorPath')}
          meditationStyle={data.get('meditationStyle')}
          preview={data.get('preview', Map())}
          feature={data.get('feature', Map())}
          mediaLanguage={data.get('mediaLanguage')}
          isInPlaylist={isInPlaylist}
          itemIndex={itemIndex}
          addToPlaylist={addToPlaylist}
          upstreamContext={fullContext}
          onlyShowHero={props.onlyShowHero}
          parentSeriesTitle={data.get('seriesTitle')}
          parentSeriesId={data.get('seriesId')}
          uiLocation={uiLocation}
        />
      )
    case TYPE_CONTENT_EPISODE:
    case TYPE_CONTENT_EPISODE_YOGA:
    case TYPE_CONTENT_EPISODE_FITNESS:
    case TYPE_CONTENT_EPISODE_MEDITATION:
    case TYPE_CONTENT_EPISODE_GUIDE:
    case TYPE_CONTENT_SEGMENT:
    case TYPE_CONTENT_SEGMENT_YOGA:
    case TYPE_CONTENT_SEGMENT_FITNESS:
    case TYPE_CONTENT_SEGMENT_MEDITATION:
      return (
        <TileEpisode
          asShare={asShare}
          onClickWatch={onClickWatch}
          displayMoreInfoButton={displayMoreInfoButton}
          auth={props.auth}
          active={props.active}
          type={data.getIn(['type', 'content'])}
          id={id}
          page={props.page}
          heroLabel={props.heroLabel}
          title={data.get('title')}
          seriesTitle={data.get('seriesTitle')}
          seriesPath={data.get('seriesPath')}
          image={data.get('image')}
          episode={data.get('episode')}
          season={data.get('season')}
          vote={data.get('vote')}
          voteDown={data.get('voteDown')}
          voteId={id}
          forceAccess={forceAccess}
          label={getLabel(
            staticText,
            TYPE_CONTENT_EPISODE,
            data.get('isNew'),
            data.get('featuredTileType'),
            data.get('featuredTileLabel'),
            null,
            isGuide,
            isEvent,
            { isLiveAccessUpgradeToWatch },
          )}
          teaser={showTeaser ? data.get('teaser') : null}
          duration={data.get('duration')}
          userInfo={data.get('userInfo')}
          url={data.get('url', data.get('path', '/'))}
          host={data.get('host')}
          yogaStyle={data.get('yogaStyle')}
          yogaLevel={data.get('yogaLevel')}
          yogaLevelPath={data.get('yogaLevelPath')}
          yogaStylePath={data.get('yogaStylePath')}
          yogaDurationPath={data.get('yogaDurationPath')}
          yogaTeacherPath={data.get('yogaTeacherPath')}
          fitnessStyle={data.get('fitnessStyle')}
          fitnessLevel={data.get('fitnessLevel')}
          fitnessLevelPath={data.get('fitnessLevelPath')}
          fitnessStylePath={data.get('fitnessStylePath')}
          fitnessDurationPath={data.get('fitnessDurationPath')}
          fitnessInstructorPath={data.get('fitnessInstructorPath')}
          meditationStyle={data.get('meditationStyle')}
          showMoreInfo={props.showMoreInfo}
          preview={data.get('preview', Map())}
          feature={data.get('feature', Map())}
          mediaLanguage={data.get('mediaLanguage')}
          upgradeToWatch={isLiveAccessUpgradeToWatch}
          isInPlaylist={isInPlaylist}
          addToPlaylist={addToPlaylist}
          upstreamContext={fullContext}
          onlyShowHero={props.onlyShowHero}
          itemIndex={itemIndex}
          isGuide={isGuide}
          parentSeriesTitle={data.get('seriesTitle')}
          parentSeriesId={data.get('seriesId')}
          uiLocation={uiLocation}
        />
      )
    case TYPE_CONTENT_SERIES:
    case TYPE_CONTENT_SERIES_YOGA:
    case TYPE_CONTENT_SERIES_FITNESS:
    case TYPE_CONTENT_SERIES_MEDITATION:
    case TYPE_CONTENT_SERIES_GUIDE:
    case TYPE_CONTENT_SEGMENTED:
    case TYPE_CONTENT_SEGMENTED_YOGA:
    case TYPE_CONTENT_SEGMENTED_FITNESS:
    case TYPE_CONTENT_SEGMENTED_MEDITATION:
      return (
        <TileSeries
          onClickWatch={onClickWatch}
          displayMoreInfoButton={displayMoreInfoButton}
          auth={props.auth}
          active={props.active}
          id={id}
          page={props.page}
          type={data.getIn(['type', 'content'])}
          title={data.get('title')}
          image={vertical ? data.get('verticalImage') : data.get('image')}
          vote={data.get('vote')}
          voteDown={data.get('voteDown')}
          voteId={id}
          episodeCount={data.get('episodeCount')}
          seasonCount={data.get('seasonCount')}
          studio={data.get('studio')}
          label={getLabel(
            staticText,
            TYPE_CONTENT_SERIES,
            data.get('isNew'),
            data.get('featuredTileType'),
            data.get('featuredTileLabel'),
            data.get('hasNewEpisodes'),
            isGuide,
            isEvent,
          )}
          teaser={data.get('teaser')}
          url={data.get('url', '')}
          host={data.get('host')}
          showMoreInfo={props.showMoreInfo}
          preview={data.get('preview', Map())}
          feature={data.get('feature', Map())}
          mediaLanguage={data.get('mediaLanguage')}
          upstreamContext={fullContext}
          vertical={vertical}
          onlyShowHero={props.onlyShowHero}
          itemIndex={itemIndex}
          isGuide={isGuide}
          isEvent={isEvent}
          uiLocation={uiLocation}
        />
      )
    case TYPE_CONTENT_SERIES_LINK:
      return <TileSeriesLink url={data.get('path') || ''} />
    case TYPE_CONTENT_SUBCATEGORY:
      return (
        <TileSubcategory
          title={data.get('title')}
          image={data.get('image')}
          url={data.get('url')}
        />
      )
    case TYPE_CONTENT_PRODUCT:
      return (
        <TileProduct
          auth={props.auth}
          id={id}
          type={type}
          title={data.get('title')}
          image={data.get('image') || data.get('verticalImage', '')}
          url={data.getIn(['cta', 'path'], '')}
          preview={data.get('preview', Map())}
          feature={data.get('feature', Map())}
        />
      )
    default:
      return <TilePlaceholder />
  }
}

class Tile extends React.Component {
  constructor (props) {
    super(props)

    this.fullContext = props.upstreamContext ? props.upstreamContext.mergeDeep(fromJS({
      source: props.tileData.getIn(['reason', 'source'], null),
      score: props.tileData.getIn(['reason', 'score'], null),
      videoTasteSegment: props.tileData.getIn(['reason', 'videoTasteSegment'], null),
      itemIndex: props.itemIndex,
      rowType: props.rowType,
      rowIndex: props.rowIndex,
      destId: props.destId,
      campaignId: props.campaignId,
      displayRowIndex: props.displayRowIndex,
    })) : null
  }

  render () {
    const { props } = this
    const {
      active,
      tileData,
      inRow,
      single,
      tileClass,
      playlistEditComponent,
      hideWatchedEditComponent,
    } = props
    const type = getTypeContent(tileData)
    const isProduct = type === TYPE_CONTENT_PRODUCT

    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    return (
      <article
        className={getClassName(
          inRow,
          single,
          tileClass,
          playlistEditComponent,
          hideWatchedEditComponent,
        )}
      >
        {hideWatchedEditComponent || playlistEditComponent}
        <div
          className={getContentClassName(
            active,
            tileData.getIn(['type', 'content']),
          )}
        >
          <TileContent
            {...props}
            fullContext={this.fullContext}
          />
          {!isProduct && <TitleFreeLabel {...props} />}
        </div>
      </article>
    )
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
  }
}

Tile.propTypes = {
  forceAccess: PropTypes.oneOf([
    ACCESS_FEATURE,
    ACCESS_PREVIEW,
  ]),
  auth: ImmutablePropTypes.map.isRequired,
  active: PropTypes.bool,
  tileClass: PropTypes.string,
  tileData: ImmutablePropTypes.map.isRequired,
  addToPlaylist: PropTypes.bool,
  showMoreInfo: PropTypes.bool.isRequired,
  showTeaser: PropTypes.bool,
  playlistEditComponent: PropTypes.element,
  hideWatchedEditComponent: PropTypes.element,
  onClickWatch: PropTypes.func,
  onlyShowHero: PropTypes.bool,
  heroLabel: PropTypes.string,
  inRow: PropTypes.bool,
  single: PropTypes.bool,
  isFree: PropTypes.bool,
  asShare: PropTypes.bool,
  staticText: ImmutablePropTypes.map.isRequired,
  upstreamContext: ImmutablePropTypes.map,
}

export default connect((state) => ({
  user: state.user,
  staticText: state.staticText.getIn(['data', 'tile', 'data']),
}))(Tile)
